import React from 'react';

/**
 * AdminCreateAppointmentModal Component
 * Modal for admins to create or edit appointments with patient and service worker selection
 * 
 * Props:
 * - isOpen: Boolean to show/hide modal
 * - formData: Object containing form state
 * - onFormChange: Callback function to update form data
 * - onSubmit: Callback function when form is submitted
 * - onClose: Callback function to close modal
 * - availableServices: Array of available service names
 * - availablePatients: Array of available patients
 * - availableServiceWorkers: Array of available service workers
 * - isLoading: Boolean to show loading state during submission
 * - error: Error message to display
 * - isEditMode: Boolean indicating if modal is in edit mode
 */
export default function AdminCreateAppointmentModal({
    isOpen,
    formData,
    onFormChange,
    onSubmit,
    onClose,
    availableServices = [],
    availablePatients = [],
    availableServiceWorkers = [],
    isLoading = false,
    error = null,
    isEditMode = false
}) {
    if (!isOpen) {
        return null;
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'psw_id') {
            const selectedWorker = availableServiceWorkers.find(
                (worker) => (worker.user_id || worker.psw_id || worker.id) === value
            );
            onFormChange({
                ...formData,
                psw_id: value,
                psw_name: selectedWorker ? `${selectedWorker.first_name || ''} ${selectedWorker.last_name || ''}`.trim() || selectedWorker.username : '',
            });
        } else {
            onFormChange({
                ...formData,
                [name]: value,
            });
        }
    };

    const handleCheckboxChange = (serviceName) => {
        const currentServices = formData.services || [];
        let updatedServices;
        
        if (currentServices.includes(serviceName)) {
            updatedServices = currentServices.filter(s => s !== serviceName);
        } else {
            updatedServices = [...currentServices, serviceName];
        }
        
        onFormChange({
            ...formData,
            services: updatedServices
        });
    };

    const handleTextareaChange = (e) => {
        const { name, value } = e.target;
        onFormChange({
            ...formData,
            [name]: value
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEditMode ? 'Edit Appointment' : 'Create New Appointment'}</h2>
                    <button 
                        className="modal-close" 
                        onClick={onClose} 
                        aria-label="Close modal"
                        disabled={isLoading}
                    >
                        ×
                    </button>
                </div>
                
                <form onSubmit={onSubmit} className="modal-form">
                    {error && (
                        <div style={{
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                            padding: '12px',
                            borderRadius: '4px',
                            marginBottom: '20px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Patient Selection */}
                    <div className="form-group">
                        <label htmlFor="admin-patient-id">Patient *</label>
                        <select
                            id="admin-patient-id"
                            name="user_id"
                            value={formData.user_id}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                            disabled={isLoading}
                        >
                            <option value="">Select a patient</option>
                            {availablePatients.map(patient => (
                                <option key={patient.user_id || patient.id} value={patient.user_id || patient.id}>
                                    {`${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.username}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Service Worker Selection (Optional) */}
                    <div className="form-group">
                        <label htmlFor="admin-psw-id">Service Worker (Optional)</label>
                        <select
                            id="admin-psw-id"
                            name="psw_id"
                            value={formData.psw_id}
                            onChange={handleInputChange}
                            className="form-input"
                            disabled={isLoading}
                        >
                            <option value="">No service worker assigned</option>
                            {availableServiceWorkers.map(worker => (
                                <option key={worker.user_id || worker.psw_id || worker.id} value={worker.user_id || worker.psw_id || worker.id}>
                                    {`${worker.first_name || ''} ${worker.last_name || ''}`.trim() || worker.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Date Field */}
                    <div className="form-group">
                        <label htmlFor="admin-appointment-date">Date *</label>
                        <input
                            type="date"
                            id="admin-appointment-date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                            className="form-input"
                            // min={new Date().toLocaleDateString('en-CA')}
                            max="2099-12-31"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Start Time Field */}
                    <div className="form-group">
                        <label htmlFor="admin-start-time">Start Time *</label>
                        <input
                            type="time"
                            id="admin-start-time"
                            name="start_time"
                            value={formData.start_time}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* End Time Field */}
                    <div className="form-group">
                        <label htmlFor="admin-end-time">End Time *</label>
                        <input
                            type="time"
                            id="admin-end-time"
                            name="end_time"
                            value={formData.end_time}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Services Field - Checkboxes */}
                    <div className="form-group">
                        <label>Services *</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                            {availableServices.length > 0 ? (
                                availableServices.map(service => (
                                    <label key={service} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={(formData.services || []).includes(service)}
                                            onChange={() => handleCheckboxChange(service)}
                                            disabled={isLoading}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <span>{service}</span>
                                    </label>
                                ))
                            ) : (
                                <p style={{ color: '#999', fontSize: '14px' }}>No services available</p>
                            )}
                        </div>
                    </div>

                    {/* Location Field */}
                    <div className="form-group">
                        <label htmlFor="admin-location">Location *</label>
                        <input
                            type="text"
                            id="admin-location"
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Enter appointment location"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Description Field */}
                    <div className="form-group">
                        <label htmlFor="admin-description">Description</label>
                        <textarea
                            id="admin-description"
                            name="description"
                            value={formData.description}
                            onChange={handleTextareaChange}
                            className="form-textarea"
                            placeholder="Enter appointment description and specific services"
                            disabled={isLoading}
                            rows="4"
                        />
                    </div>

                    {/* Form Actions */}
                    <div className="modal-actions">
                        <button 
                            type="button" 
                            className="modal-button-cancel" 
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="modal-button-submit"
                            disabled={isLoading}
                        >
                            {isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Appointment' : 'Create Appointment')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
